import "./SkillList.css"
import { type ReactNode, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { getSkillDescription, getSkillName } from "../i18n/gameContent"
import { useI18n } from "../i18n/useI18n"
import { SKILL_MAP } from "../match/engine/content/skills"
import type { CardColor, SkillDefinition, SkillSelection } from "../schemas"
import { CostBadge } from "./CostBadge"
import { FACTION_PALETTE } from "./cardTheme"
import { usePortalTarget } from "./PortalTarget"
import { AllyIcon, DoubleAllyIcon, ExhaustIcon, SKILL_ICON } from "./SkillIcon"
import { getSkillModifierLabels } from "./skillText"

type SkillListRowRenderArgs = {
  definition?: SkillDefinition
  index: number
  selection: SkillSelection
  skillName: string
}

type SkillListProps = {
  skills: SkillSelection[]
  primaryColor: CardColor
  className?: string
  showDetailsOnHover?: boolean
  renderControls?: (args: SkillListRowRenderArgs) => ReactNode
}

function skillAffinityColor(
  skillId: SkillSelection["skillId"],
  primary: CardColor,
) {
  const def = SKILL_MAP.get(skillId)
  if (!def || def.isColorless) return FACTION_PALETTE.colorless.muted
  return (
    FACTION_PALETTE[def.affinity as CardColor]?.muted ??
    FACTION_PALETTE[primary].muted
  )
}

function ModifierChips({ selection }: { selection: SkillSelection }) {
  const { t } = useI18n()
  const definition = SKILL_MAP.get(selection.skillId)

  return (
    <span className="skill-list-row-modifiers">
      {selection.mode === "non_exhausting" && (
        <span
          className="skill-list-modifier-chip"
          title={t("cardFace.skill.nonExhausting")}
        >
          ★
        </span>
      )}
      {selection.mode === "self_destruct" && (
        <span
          className="skill-list-modifier-chip"
          title={t("cardFace.skill.overload")}
        >
          ✕
        </span>
      )}
      {selection.allyRequirement === 1 && (
        <span
          className="skill-list-modifier-chip"
          title={t("cardFace.skill.ally.one")}
        >
          <AllyIcon size={11} strokeWidth={2} />
        </span>
      )}
      {selection.allyRequirement === 2 && (
        <span
          className="skill-list-modifier-chip"
          title={t("cardFace.skill.ally.two")}
        >
          <DoubleAllyIcon size={11} strokeWidth={2} />
        </span>
      )}
      {selection.mode == null && definition?.isPassive === false && (
        <span
          className="skill-list-modifier-chip"
          title={t("cardFace.skill.exhaust")}
        >
          <ExhaustIcon size={11} strokeWidth={2} />
        </span>
      )}
    </span>
  )
}

function SkillDetailPopover({
  anchorRect,
  primaryColor,
  selection,
}: {
  anchorRect: DOMRect
  primaryColor: CardColor
  selection: SkillSelection
}) {
  const { t } = useI18n()
  const portalTarget = usePortalTarget()
  const definition = SKILL_MAP.get(selection.skillId)
  const Icon = SKILL_ICON[selection.skillId] ?? ExhaustIcon
  const color = skillAffinityColor(selection.skillId, primaryColor)
  const modifierLabels = getSkillModifierLabels(t, selection)
  const top = Math.max(
    8,
    Math.min(anchorRect.bottom + 8, window.innerHeight - 220),
  )
  const left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - 336))

  return createPortal(
    <div className="skill-list-detail-popover" style={{ top, left }}>
      <div className="skill-list-detail-header">
        <span className="skill-list-detail-icon" style={{ color }}>
          <Icon size={18} strokeWidth={2} />
        </span>
        <span className="skill-list-detail-title">
          {getSkillName(t, selection.skillId)}
        </span>
        <span className="skill-list-detail-meta">
          <span>
            {definition?.isPassive
              ? t("skillsEditor.skillType.passive")
              : t("common.active")}
          </span>
          <span>·</span>
          <CostBadge
            cost={definition?.baseCostDelta ?? 0}
            size="s"
            variant="subtle"
          />
        </span>
      </div>
      <p className="skill-list-detail-description">
        {getSkillDescription(t, selection.skillId)}
      </p>
      {modifierLabels.length > 0 && (
        <div className="skill-list-detail-modifiers">
          {modifierLabels.map((label) => (
            <span className="skill-list-detail-modifier" key={label}>
              {label}
            </span>
          ))}
        </div>
      )}
    </div>,
    portalTarget,
  )
}

export function SkillList({
  skills,
  primaryColor,
  className = "",
  showDetailsOnHover = false,
  renderControls,
}: SkillListProps) {
  const { t } = useI18n()
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [hoveredRect, setHoveredRect] = useState<DOMRect | null>(null)

  const hoveredSelection =
    hoveredIndex == null ? null : (skills[hoveredIndex] ?? null)
  const listClassName = useMemo(
    () => ["skills-editor-list", className].filter(Boolean).join(" "),
    [className],
  )

  return (
    <>
      <div className={listClassName}>
        {skills.map((selection, index) => {
          const Icon = SKILL_ICON[selection.skillId] ?? ExhaustIcon
          const definition = SKILL_MAP.get(selection.skillId)
          const color = skillAffinityColor(selection.skillId, primaryColor)
          const skillName = getSkillName(t, selection.skillId)
          const rowContent = (
            <>
              <span className="skills-editor-row-icon" style={{ color }}>
                <Icon size={15} strokeWidth={2} />
              </span>
              <span className="skills-editor-row-name">{skillName}</span>
              <span className="skills-editor-row-meta">
                {definition?.isPassive
                  ? t("skillsEditor.skillType.passive")
                  : t("common.active")}{" "}
                ·{" "}
                <CostBadge
                  className="skills-editor-row-cost"
                  cost={definition?.baseCostDelta ?? 0}
                  size="s"
                  variant="subtle"
                />
              </span>
              <ModifierChips selection={selection} />
              {renderControls?.({
                definition,
                index,
                selection,
                skillName,
              })}
            </>
          )

          if (showDetailsOnHover) {
            return (
              <button
                className="skills-editor-row skill-list-row-interactive"
                key={selection.skillId}
                onBlur={() => {
                  setHoveredIndex(null)
                  setHoveredRect(null)
                }}
                onFocus={(event) => {
                  setHoveredIndex(index)
                  setHoveredRect(event.currentTarget.getBoundingClientRect())
                }}
                onMouseEnter={(event) => {
                  setHoveredIndex(index)
                  setHoveredRect(event.currentTarget.getBoundingClientRect())
                }}
                onMouseLeave={() => {
                  setHoveredIndex(null)
                  setHoveredRect(null)
                }}
                type="button"
              >
                {rowContent}
              </button>
            )
          }

          return (
            <div className="skills-editor-row" key={selection.skillId}>
              {rowContent}
            </div>
          )
        })}
      </div>
      {showDetailsOnHover && hoveredSelection && hoveredRect && (
        <SkillDetailPopover
          anchorRect={hoveredRect}
          primaryColor={primaryColor}
          selection={hoveredSelection}
        />
      )}
    </>
  )
}
